import { memo, useCallback, useRef, useState } from "react";
import {
  Node,
  NodeProps,
  NodeResizer,
  ResizeDragEvent,
  ResizeParams,
} from "@xyflow/react";
import { Colors, Text } from "@zapier/design-system";
import { useAtomValue, useSetAtom } from "jotai";
import {
  groupNodeCreateStartAtom,
  isDraggingNodeAtom,
  nodeClassNameAtomFamily,
  saveNodeOnResizeEndSetter,
  shouldShowStepIdsAtom,
  singleSelectedNodeIdAtom,
  GroupNodeData,
} from "@zapier/canvas-state";

import { GroupIcon } from "../../../icons/GroupIcon";
import useUpdateNode from "../../../../hooks/useUpdateNode";
import { StepId } from "../../../../components/StepId";

import { FormInput, DEFAULT_GROUP_LABEL } from "./FormInput";
import { getNodeResizerHandleStyle, getNodeResizerLineStyle } from "../utils";
import { GroupNodeFloatingToolbar } from "./GroupNodeFloatingToolbar";
import { lightenColor } from "../../../../utils/lightenColor";
import styles from "./GroupNode.module.css";
import classNames from "classnames";

function GroupNode({ id, selected, data }: NodeProps<Node<GroupNodeData>>) {
  const [isEditing, setIsEditing] = useState(false);
  const color = data.color ?? Colors.GrayWarm5;
  const [label, setLabel] = useState(data.label);
  const updateNodeAndSave = useUpdateNode();
  const ref = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const className = useAtomValue(nodeClassNameAtomFamily(id));
  const shouldHighlight = className?.includes("highlight");
  const isDraggingNode = useAtomValue(isDraggingNodeAtom);
  const groupNodeCreateStart = useAtomValue(groupNodeCreateStartAtom);
  const singleSelectedNodeId = useAtomValue(singleSelectedNodeIdAtom);
  const isSelected =
    !groupNodeCreateStart && !isDraggingNode && singleSelectedNodeId === id;
  const saveOnResizeEnd = useSetAtom(saveNodeOnResizeEndSetter);

  const borderColor = data.color === Colors.PrimeWhite ? "#D7D3C9" : color;

  function getBackgroundColor() {
    if (color === Colors.PrimeWhite) {
      return Colors.PrimeWhite;
    }

    // This is the default
    if (color === Colors.GrayWarm5) {
      return "#E8E7E480";
    }

    return lightenColor(color);
  }

  const origBackgroundColor = getBackgroundColor();
  const backgroundColor = shouldHighlight
    ? lightenColor(origBackgroundColor, 0.5, 1)
    : origBackgroundColor;

  const handleEditLabel = useCallback(
    (value: string) => {
      setIsEditing(false);
      updateNodeAndSave({
        data: {
          label: value,
        },
      });
    },
    [setIsEditing, updateNodeAndSave],
  );

  const handleStartEditing = useCallback((event: React.MouseEvent) => {
    // Stop event propagation to prevent reactflow from putting group node in selected state
    event.stopPropagation();
    setIsEditing(true);
    if (ref.current) {
      setTimeout(() => {
        ref.current?.focus();
      }, 0);
    }
  }, []);

  const handleResizeEnd = useCallback(
    (_e: ResizeDragEvent, { width, height, x, y }: ResizeParams) => {
      saveOnResizeEnd({ id, width, height, x, y });
    },
    [saveOnResizeEnd, id],
  );

  return (
    <>
      {isSelected && (
        <GroupNodeFloatingToolbar
          id={id}
          color={color}
          labelRef={labelRef}
          nodeRef={nodeRef}
        />
      )}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions*/}
      <div
        ref={labelRef}
        className={classNames(styles.label, {
          [styles.notEditing]: !isEditing,
          [styles.editing]: isEditing,
          ["nodrag nopan"]:
            isEditing /* Prevents dragging the node and panning/zooming the canvas when editing, allowing text selection */,
        })}
        onDoubleClick={handleStartEditing}
        onClick={handleStartEditing}
      >
        <div className={styles.iconWrapper}>
          <GroupIcon fill={Colors.GrayWarm10} height="14px" width="14px" />
        </div>
        {!isEditing && (
          <div className={styles.truncateText}>
            <Text type="SmallPrint3">{data.label || DEFAULT_GROUP_LABEL}</Text>
          </div>
        )}
        {isEditing && (
          <FormInput
            type="text"
            label="Asset Name"
            ref={ref}
            maxLength={50}
            value={label as string}
            onBlur={(e) =>
              handleEditLabel((e.target as HTMLInputElement).value)
            }
            onChange={(value) => setLabel(value)}
            onSubmit={() => handleEditLabel(label)}
          />
        )}
      </div>
      <NodeResizer
        handleStyle={getNodeResizerHandleStyle()}
        lineStyle={getNodeResizerLineStyle()}
        isVisible={selected}
        onResizeEnd={handleResizeEnd}
      />
      <div
        ref={nodeRef}
        className={styles.wrapper}
        style={{
          border: shouldHighlight
            ? `2px solid ${Colors.UiPrimaryStrongest}`
            : `1px solid ${borderColor}`,
          backgroundColor,
        }}
      />
      {shouldShowStepIds && <StepId />}
    </>
  );
}

export default memo(GroupNode);
